import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "input": {
 *     "mode": "text2video",
 *     "video_url": "https://example.com/input_video.mp4",
 *     "text": "Hello, this is a lip sync test.",
 *     "voice_id": "voice_001",
 *     "voice_language": "en",
 *     "voice_speed": 1
 *   }
 * }
 * ```
 */
export interface KlingLipsyncParams {
  /**
   * 输入参数对象（必填）
   */
  input: {
    /**
     * 生成模式（必填），支持: text2video（文本到视频）, audio2video（音频到视频）
     * @values text2video | audio2video
     */
    mode: 'text2video' | 'audio2video';
    /**
     * 视频ID（与video_url二选一，不能同时指定）
     */
    video_id?: string;
    /**
     * 视频URL（与video_id二选一，不能同时指定），必须是有效的HTTP或HTTPS URL
     */
    video_url?: string;
    /**
     * 文本内容（text2video模式必填），最大长度120字符
     */
    text?: string;
    /**
     * 声音ID（text2video模式必填）
     */
    voice_id?: string;
    /**
     * 声音语言（text2video模式必填），支持: zh（中文）, en（英文）
     * @values zh | en
     */
    voice_language?: 'zh' | 'en';
    /**
     * 声音速度（可选），范围0.8-2.0，默认1.0
     * @range 0.8 - 2
     */
    voice_speed?: number;
    /**
     * 音频类型（audio2video模式必填），支持: file（文件）, url（URL）
     * @values file | url
     */
    audio_type?: 'file' | 'url';
    /**
     * 音频文件（audio_type为file时必填）
     */
    audio_file?: string;
    /**
     * 音频URL（audio_type为url时必填），必须是有效的HTTP或HTTPS URL
     */
    audio_url?: string;
  };
}

export interface KlingLipsyncResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_lipsync
 *
 * Kling AI 唇形同步生成接口，支持文本到视频(text2video)和音频到视频(audio2video)两种模式。
 * 
 * Model: kling-lipsync
 * Features:
 * - 文本到视频(text2video): 根据文本和声音ID生成唇形同步视频
 *   - 支持中文和英文
 *   - 文本长度最多120字符
 *   - 支持调节声音速度（0.8-2.0倍）
 * - 音频到视频(audio2video): 根据音频文件或URL生成唇形同步视频
 *   - 支持音频文件上传
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingLipsync } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingLipsync(
 *   {
 *     "input": {
 *       "mode": "text2video",
 *       "video_url": "https://example.com/input_video.mp4",
 *       "text": "Hello, this is a lip sync test.",
 *       "voice_id": "voice_001",
 *       "voice_language": "en",
 *       "voice_speed": 1
 *     }
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingLipsyncParams 接口定义
 * @returns 生成的资源列表
 */
export async function klingLipsync(
  params: KlingLipsyncParams,
): Promise<KlingLipsyncResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_lipsync',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingLipsyncResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
